import { useEffect } from 'react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { DocumentItem } from '../types';

// ---------------------------------------------------------------------------
// Document type mapping (common KYC document types)
// ---------------------------------------------------------------------------

const DOCUMENT_TYPE_LABELS: Record<number, string> = {
  1: 'Passport',
  2: 'National ID',
  3: 'Proof of Address',
  4: 'Certificate of Incorporation',
  5: 'Bank Statement',
  6: 'Tax Certificate',
  7: 'Power of Attorney',
  8: 'Other',
};

function getDocumentTypeLabel(typeId: number): string {
  return DOCUMENT_TYPE_LABELS[typeId] ?? `Type ${typeId}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DocumentsTabProps {
  documents: DocumentItem[];
  loading: boolean;
  error: string | null;
  onLoad: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentsTab({ documents, loading, error, onLoad }: DocumentsTabProps) {
  useEffect(() => {
    void onLoad();
  }, [onLoad]);

  if (loading) {
    return <LoadingSpinner size="md" text="Loading documents..." />;
  }

  if (error) {
    return (
      <div className="rounded-xl border border-[#ffcdd2] bg-[#ffebee] p-6 text-center text-[#c62828]">
        {error}
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="rounded-xl border border-[#eee] bg-white p-6 text-center text-[#999]">
        No documents uploaded
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-[#eee] bg-white">
      <table className="w-full text-left text-[13px]">
        <thead>
          <tr className="border-b border-[#eee] bg-[#fafafa]">
            <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.5px] text-[#999]">
              Name
            </th>
            <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.5px] text-[#999]">
              Type
            </th>
            <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.5px] text-[#999]">
              Person
            </th>
            <th className="px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.5px] text-[#999]">
              Expiry Date
            </th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => (
            <tr key={doc.documentId} className="border-b border-[#f5f5f5] last:border-none">
              <td className="px-4 py-3 font-medium text-[#1a1a1a]">{doc.name}</td>
              <td className="px-4 py-3 text-[#666]">{getDocumentTypeLabel(doc.documentTypeId)}</td>
              <td className="px-4 py-3 text-[#666]">
                {doc.personId != null ? `Person #${doc.personId}` : '—'}
              </td>
              <td className="px-4 py-3 text-[#666]">
                {doc.expiryDate
                  ? new Date(doc.expiryDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      timeZone: 'UTC',
                    })
                  : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
